import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, Mail, SlidersHorizontal, Check, X, RefreshCw, Inbox } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api, { mensajeDeError, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Secciones operativas del panel de administración: reservas, mensajes de
 * contacto, cola de correo y ajustes de la plataforma.
 *
 * Ninguna de estas vistas expone datos del paciente más allá de su seudónimo.
 * El administrador no puede ver correos de pacientes ni enlaces de sesión.
 */
export const AdminOperacion = () => {
  const [seccion, setSeccion] = useState('reservas');

  const [reservas, setReservas] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [correos, setCorreos] = useState(null);
  const [ajustes, setAjustes] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      if (seccion === 'reservas') {
        const { data } = await api.get('/admin/reservas');
        setReservas(data);
      } else if (seccion === 'mensajes') {
        const { data } = await api.get('/admin/mensajes');
        setMensajes(data);
      } else if (seccion === 'correos') {
        const { data } = await api.get('/admin/correos');
        setCorreos(data);
      } else if (seccion === 'ajustes') {
        const { data } = await api.get('/admin/ajustes');
        setAjustes(data);
      }
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos cargar esta sección'));
    } finally {
      setCargando(false);
    }
  }, [seccion]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cancelarReserva = async (id) => {
    try {
      await api.patch(`/admin/reservas/${id}/cancelar`);
      toast.success('Reserva cancelada');
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos cancelar la reserva'));
    }
  };

  const confirmarPago = async (id) => {
    try {
      await api.patch(`/admin/reservas/${id}/pago`, { status: 'paid' });
      toast.success('Pago confirmado');
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos confirmar el pago'));
    }
  };

  const marcarAtendido = async (id) => {
    try {
      await api.patch(`/admin/mensajes/${id}`);
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos actualizar el mensaje'));
    }
  };

  const reintentarCorreo = async (id) => {
    try {
      await api.post(`/admin/correos/${id}/reintentar`);
      toast.success('Se reintentará en la próxima ejecución');
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos reintentar el envío'));
    }
  };

  const guardarAjustes = async (cambios) => {
    try {
      const { data } = await api.patch('/admin/ajustes', cambios);
      setAjustes((actual) => ({ ...actual, settings: data.settings }));
      toast.success('Ajustes guardados');
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos guardar los ajustes'));
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <CardTitle className="text-white">Operación</CardTitle>
        <CardDescription className="text-slate-400">
          Reservas, mensajes, correos y configuración de la plataforma.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Tabs value={seccion} onValueChange={setSeccion}>
          <TabsList className="mb-6 bg-slate-900 flex-wrap h-auto">
            <TabsTrigger value="reservas" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Reservas
            </TabsTrigger>
            <TabsTrigger value="mensajes" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <Inbox className="w-4 h-4 mr-2" />
              Mensajes
            </TabsTrigger>
            <TabsTrigger value="correos" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" />
              Correos
            </TabsTrigger>
            <TabsTrigger value="ajustes" className="text-slate-300 data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Ajustes
            </TabsTrigger>
          </TabsList>

          {cargando && <p className="text-slate-400 py-6">Cargando…</p>}

          {/* ---------- Reservas ---------- */}
          <TabsContent value="reservas">
            {!cargando && reservas.length === 0 && (
              <p className="text-slate-400 py-8 text-center">Todavía no hay reservas.</p>
            )}

            <div className="space-y-2">
              {reservas.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3 px-4 rounded-lg bg-slate-900/60"
                >
                  <div className="min-w-0">
                    <p className="text-white font-medium">
                      {r.patient_pseudonym}{' '}
                      <span className="text-slate-400 font-normal">con</span>{' '}
                      {r.psychologist_name}
                    </p>
                    <p className="text-sm text-slate-400">
                      {format(parseISO(r.scheduled_date), "d MMM yyyy, HH:mm", { locale: es })} hrs ·{' '}
                      {pesos(r.amount)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      className={`border-0 ${
                        r.status === 'cancelled'
                          ? 'bg-red-500/20 text-red-300'
                          : r.status === 'completed'
                            ? 'bg-slate-600/40 text-slate-300'
                            : 'bg-teal-500/20 text-teal-300'
                      }`}
                    >
                      {r.status}
                    </Badge>
                    <Badge
                      className={`border-0 ${
                        r.payment_status === 'paid'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-amber-500/20 text-amber-300'
                      }`}
                    >
                      {r.payment_status}
                    </Badge>

                    {r.payment_status !== 'paid' && r.status !== 'cancelled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmarPago(r.id)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Marcar pagada
                      </Button>
                    )}

                    {r.status !== 'cancelled' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelarReserva(r.id)}
                        className="text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ---------- Mensajes ---------- */}
          <TabsContent value="mensajes">
            {!cargando && mensajes.length === 0 && (
              <p className="text-slate-400 py-8 text-center">No hay mensajes de contacto.</p>
            )}

            <div className="space-y-3">
              {mensajes.map((m) => (
                <div key={m.id} className={`p-4 rounded-lg ${m.handled ? 'bg-slate-900/40' : 'bg-slate-900/80'}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                      <Badge className="bg-slate-700 text-slate-200 border-0 mb-1">{m.reason}</Badge>
                      <p className="text-sm text-slate-400">
                        {m.email || 'sin correo'} ·{' '}
                        {format(parseISO(m.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                      </p>
                    </div>

                    {!m.handled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => marcarAtendido(m.id)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Atendido
                      </Button>
                    )}
                  </div>
                  <p className="text-slate-200 whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ---------- Correos ---------- */}
          <TabsContent value="correos">
            {correos && (
              <>
                <div className="flex flex-wrap gap-3 mb-6">
                  <Badge className="bg-slate-700 text-slate-200 border-0">
                    Proveedor: {correos.driver}
                  </Badge>
                  {Object.entries(correos.by_status || {}).map(([estado, total]) => (
                    <Badge key={estado} className="bg-slate-700 text-slate-200 border-0">
                      {estado}: {total}
                    </Badge>
                  ))}
                </div>

                {correos.driver === 'log' && (
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-6">
                    <p className="text-amber-200 text-sm">
                      Los correos no se están enviando: el proveedor está en modo{' '}
                      <strong>log</strong>. Se encolan y quedan registrados. Configura un
                      proveedor en <code>api/config.php</code> para que salgan de verdad.
                    </p>
                  </div>
                )}

                {(correos.failed_list?.length ?? 0) === 0 ? (
                  <p className="text-slate-400 py-6 text-center">No hay correos fallidos.</p>
                ) : (
                  <div className="space-y-2">
                    {correos.failed_list.map((c) => (
                      <div
                        key={c.id}
                        className="flex flex-wrap items-center justify-between gap-3 py-3 px-4 rounded-lg bg-slate-900/60"
                      >
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">{c.subject}</p>
                          <p className="text-xs text-slate-400">
                            {c.to} · {c.attempts} intentos · {c.last_error}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reintentarCorreo(c.id)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Reintentar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ---------- Ajustes ---------- */}
          <TabsContent value="ajustes">
            {ajustes && <FormularioAjustes ajustes={ajustes} onGuardar={guardarAjustes} />}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const CAMPOS = [
  { clave: 'cancellation_hours', etiqueta: 'Horas mínimas para cancelar', ayuda: 'Con cuánta antelación el paciente puede cancelar o cambiar su hora.' },
  { clave: 'min_booking_hours', etiqueta: 'Horas mínimas para reservar', ayuda: 'Cuánto antes de la sesión se puede tomar una hora.' },
  { clave: 'booking_horizon_days', etiqueta: 'Días hacia adelante', ayuda: 'Hasta cuándo se puede reservar.' },
  { clave: 'commission_percent', etiqueta: 'Comisión de la plataforma (%)', ayuda: 'Se descuenta del pago al profesional.' },
  { clave: 'room_open_minutes_before', etiqueta: 'Minutos antes de abrir la sala', ayuda: 'Cuándo se activa el enlace de la videollamada.' },
  { clave: 'email_retention_days', etiqueta: 'Días para borrar el correo del paciente', ayuda: 'Tras la sesión, el correo se elimina solo. Cero desactiva el borrado.' },
];

const FormularioAjustes = ({ ajustes, onGuardar }) => {
  const [valores, setValores] = useState(ajustes.settings);

  const cambiar = (clave, valor) => setValores((a) => ({ ...a, [clave]: valor }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Badge className="bg-slate-700 text-slate-200 border-0">
          Pagos: {ajustes.payments_driver}
        </Badge>
        <Badge className="bg-slate-700 text-slate-200 border-0">
          Correos: {ajustes.mail_driver}
        </Badge>
      </div>

      {ajustes.payments_driver === 'mock' && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-red-200 text-sm">
            Los pagos están en modo <strong>simulado</strong>: las reservas se confirman sin
            cobrar nada. No dejes esto así en producción.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-900/60">
        <div>
          <p className="text-white font-medium">Reservas abiertas</p>
          <p className="text-sm text-slate-400">
            Si lo apagas, nadie puede agendar. El sitio sigue arriba.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            const nuevo = valores.bookings_open ? 0 : 1;
            cambiar('bookings_open', nuevo);
            onGuardar({ bookings_open: nuevo });
          }}
          className={`border-slate-600 ${valores.bookings_open ? 'text-green-400' : 'text-red-400'}`}
        >
          {valores.bookings_open ? 'Abiertas' : 'Cerradas'}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {CAMPOS.map((campo) => (
          <div key={campo.clave}>
            <Label htmlFor={campo.clave} className="text-slate-300">
              {campo.etiqueta}
            </Label>
            <Input
              id={campo.clave}
              type="number"
              value={valores[campo.clave] ?? 0}
              onChange={(e) => cambiar(campo.clave, Number(e.target.value))}
              className="mt-1 bg-slate-900 border-slate-700 text-white"
            />
            <p className="text-xs text-slate-400 mt-1">{campo.ayuda}</p>
          </div>
        ))}
      </div>

      <div>
        <Label htmlFor="contact_inbox" className="text-slate-300">
          Correo donde llegan los mensajes de contacto
        </Label>
        <Input
          id="contact_inbox"
          type="email"
          value={valores.contact_inbox || ''}
          onChange={(e) => cambiar('contact_inbox', e.target.value)}
          placeholder="contacto@anonimamente.cl"
          className="mt-1 bg-slate-900 border-slate-700 text-white"
        />
      </div>

      <Button
        onClick={() => onGuardar(valores)}
        className="bg-teal-600 hover:bg-teal-700 text-white"
      >
        Guardar ajustes
      </Button>
    </div>
  );
};

export default AdminOperacion;
